// React hooks for settings management
import { useState, useEffect, useCallback } from 'react';
import { settingsService } from '../services/settingsService';
import { SettingKey, DEFAULT_SETTINGS, AdminPermissions } from '../types/settings';

/**
 * Hook for accessing app settings
 */
export function useSettings(fid?: string) {
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const settingsData = fid 
        ? await settingsService.getAllSettings(fid)
        : await settingsService.getPublicSettings();
      
      setSettings(settingsData);
      setLastUpdated(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load settings';
      setError(errorMessage);
      console.error('Error loading settings:', err);
      
      // Fallback to defaults
      setSettings({ ...DEFAULT_SETTINGS });
    } finally {
      setLoading(false);
    }
  }, [fid]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateSetting = useCallback(async (key: string, value: any, description?: string) => {
    if (!fid) {
      throw new Error('FID required for updating settings');
    }

    const success = await settingsService.updateSetting(key, value, fid, description);
    if (success) {
      // Refresh settings after update
      await loadSettings();
    }
    return success;
  }, [fid, loadSettings]);

  const getSetting = useCallback(<T = any>(key: SettingKey, defaultValue?: T): T => {
    const value = settings[key];
    return value !== undefined ? value as T : (defaultValue !== undefined ? defaultValue : DEFAULT_SETTINGS[key]) as T;
  }, [settings]);

  const refreshSettings = useCallback(async () => {
    await loadSettings();
  }, [loadSettings]);

  return {
    settings,
    loading,
    error,
    lastUpdated,
    getSetting,
    updateSetting,
    refreshSettings,
  };
}

/**
 * Hook for getting a specific setting value
 */
export function useSetting<T = any>(key: SettingKey, defaultValue?: T, fid?: string) {
  const [value, setValue] = useState<T>((defaultValue !== undefined ? defaultValue : DEFAULT_SETTINGS[key]) as T);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadSetting = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const settingValue = await settingsService.getSettingValue(key, defaultValue, fid);
        
        if (isMounted) {
          setValue(settingValue);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : `Failed to load setting: ${key}`;
          setError(errorMessage);
          console.error(`Error loading setting ${key}:`, err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSetting();

    return () => {
      isMounted = false;
    };
  }, [key, defaultValue, fid]);

  const updateValue = useCallback(async (newValue: T, description?: string) => {
    if (!fid) {
      throw new Error('FID required for updating settings');
    }

    const success = await settingsService.updateSetting(key, newValue, fid, description);
    if (success) {
      setValue(newValue);
    }
    return success;
  }, [key, fid]);

  return {
    value,
    loading,
    error,
    updateValue,
  };
}

/**
 * Hook for admin permissions
 */
export function useAdminPermissions(fid?: string) {
  const [permissions, setPermissions] = useState<AdminPermissions>({
    isAdmin: false,
    isSuperAdmin: false,
    canUpdateSettings: false,
    canAccessDebugMode: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadPermissions = async () => {
      if (!fid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const userPermissions = await settingsService.getAdminPermissions(fid);
        
        if (isMounted) {
          setPermissions(userPermissions);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load admin permissions';
          setError(errorMessage);
          console.error('Error loading admin permissions:', err);
          // Fallback to non-admin when API fails
          setPermissions({
            isAdmin: false,
            isSuperAdmin: false,
            canUpdateSettings: false,
            canAccessDebugMode: false,
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPermissions();

    return () => {
      isMounted = false;
    };
  }, [fid]);

  return {
    permissions,
    loading,
    error,
    isAdmin: permissions.isAdmin,
    isSuperAdmin: permissions.isSuperAdmin,
    canUpdateSettings: permissions.canUpdateSettings,
    canAccessDebugMode: permissions.canAccessDebugMode,
  };
}

/**
 * Hook for reading limits
 */
export function useReadingLimits() {
  const [limits, setLimits] = useState({
    dailyLimit: 1,
    additionalLimit: 2,
    maxTotal: 3,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadLimits = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const readingLimits = await settingsService.getReadingLimits();
        
        if (isMounted) {
          setLimits(readingLimits);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load reading limits';
          setError(errorMessage);
          console.error('Error loading reading limits:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadLimits();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    limits,
    loading,
    error,
    dailyLimit: limits.dailyLimit,
    additionalLimit: limits.additionalLimit,
    maxTotal: limits.maxTotal,
  };
}

/**
 * Hook for maintenance mode check
 */
export function useMaintenanceMode() {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const checkMaintenanceMode = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const maintenanceMode = await settingsService.isMaintenanceMode();
        
        if (isMounted) {
          setIsMaintenanceMode(maintenanceMode);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to check maintenance mode';
          setError(errorMessage);
          console.error('Error checking maintenance mode:', err);
          // Fallback to normal mode when API fails
          setIsMaintenanceMode(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkMaintenanceMode();

    // Check maintenance mode periodically (every 5 minutes)
    const interval = setInterval(checkMaintenanceMode, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  return {
    isMaintenanceMode,
    loading,
    error,
  };
}